import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, List, LayoutGrid } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/services/api';
import { toast } from 'sonner';
import { useConfirm } from '@/hooks/useConfirm';

const ServicesConfig = () => {
  const [services, setServices] = useState([]);
  const [prices, setPrices] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', duration_minutes: 60, price: 0, show_in_web: false });
  const [viewMode, setViewMode] = useState('list');
  const { confirm, ConfirmDialog } = useConfirm();

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [svcRes, priceRes] = await Promise.all([api.getServices(), api.getPrices()]);
      setServices(svcRes.data);
      setPrices(priceRes.data);
    } catch (error) {
      toast.error('Error al cargar servicios');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar campos obligatorios
    if (!formData.name || !formData.duration_minutes || formData.price === undefined) {
      toast.error('Por favor completa todos los campos obligatorios (*)');
      return;
    }
    
    try {
      if (editingItem) {
        await api.updateService(editingItem.id, { 
          name: formData.name, 
          description: formData.description?.trim() || null, 
          duration_minutes: formData.duration_minutes,
          show_in_web: formData.show_in_web
        });
        // Si el precio cambió, crear uno nuevo
        const currentPrice = prices.find(p => p.service_id === editingItem.id);
        if (!currentPrice || currentPrice.amount !== formData.price) {
          await api.createPrice({ service_id: editingItem.id, amount: formData.price, currency: 'UYU' });
        }
        toast.success('Servicio actualizado');
      } else {
        const svcResponse = await api.createService({ 
          name: formData.name, 
          description: formData.description?.trim() || null, 
          duration_minutes: formData.duration_minutes,
          show_in_web: formData.show_in_web
        });
        await api.createPrice({ service_id: svcResponse.data.id, amount: formData.price, currency: 'UYU' });
        toast.success('Servicio creado');
      }
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Error al guardar servicio');
    }
  };

  const handleEdit = (service) => {
    const price = prices.find(p => p.service_id === service.id);
    setEditingItem(service);
    setFormData({
      name: service.name,
      description: service.description || '',
      duration_minutes: service.duration_minutes,
      price: price ? price.amount : 0,
      show_in_web: service.show_in_web || false
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await confirm('Eliminar Servicio', '¿Estás seguro de eliminar este servicio? Esto también eliminará su precio asociado.');
    if (!confirmed) return;
    try {
      await api.deleteService(id);
      toast.success('Servicio eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar servicio');
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({ name: '', description: '', duration_minutes: 60, price: 0, show_in_web: false });
  };

  const getPrice = (serviceId) => {
    const price = prices.find(p => p.service_id === serviceId);
    return price ? `$ ${price.amount}` : 'Sin precio';
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Servicios</h2>
        <div className="flex space-x-2">
          <div className="flex border border-[#C9A961]/30 rounded">
            <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('list')} className={viewMode === 'list' ? 'bg-[#C9A961]' : ''}>
              <List size={16} />
            </Button>
            <Button variant={viewMode === 'cards' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('cards')} className={viewMode === 'cards' ? 'bg-[#C9A961]' : ''}>
              <LayoutGrid size={16} />
            </Button>
          </div>
          <Button onClick={() => setShowModal(true)} className="bg-[#C9A961]">
            <Plus className="mr-2" size={18} /> Nuevo
          </Button>
        </div>
      </div>

      {viewMode === 'list' ? (
        <div className="border border-[#C9A961]/20 rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Descripción</TableHead>
                <TableHead>Duración</TableHead>
                <TableHead>Precio</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {services.map((svc) => (
                <TableRow key={svc.id}>
                  <TableCell className="font-medium">{svc.name}</TableCell>
                  <TableCell>{svc.description || '-'}</TableCell>
                  <TableCell>{svc.duration_minutes} min</TableCell>
                  <TableCell className="text-[#C9A961] font-semibold">{getPrice(svc.id)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-2">
                      <Button size="sm" variant="outline" onClick={() => handleEdit(svc)}>
                        <Edit size={16} />
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDelete(svc.id)}>
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {services.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-[#6B6B6B] py-8">No hay servicios registrados</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map((svc) => (
            <Card key={svc.id} className="p-4">
              <h3 className="font-semibold text-lg">{svc.name}</h3>
              <p className="text-sm text-[#6B6B6B] mt-1">{svc.description}</p>
              <div className="mt-3 space-y-1">
                <p className="text-sm"><span className="text-[#6B6B6B]">Duración:</span> <span className="font-medium">{svc.duration_minutes} min</span></p>
                <p className="text-sm"><span className="text-[#6B6B6B]">Precio:</span> <span className="font-medium text-[#C9A961]">{getPrice(svc.id)}</span></p>
              </div>
              <div className="flex space-x-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => handleEdit(svc)}>
                  <Edit size={16} />
                </Button>
                <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDelete(svc.id)}>
                  <Trash2 size={16} />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-[#C9A961]">{editingItem ? 'Editar' : 'Nuevo'} Servicio</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Nombre *</Label><Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required /></div>
            <div><Label>Descripción</Label><Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} /></div>
            <div><Label>Duración (minutos) *</Label><Input type="number" min="15" value={formData.duration_minutes} onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value)})} required /></div>
            <div><Label>Precio ($) *</Label><Input type="number" min="0" step="0.01" value={formData.price} onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value)})} required /></div>
            <div className="flex items-center space-x-2">
              <Checkbox id="show_in_web" checked={formData.show_in_web} onCheckedChange={(checked) => setFormData({...formData, show_in_web: checked})} />
              <Label htmlFor="show_in_web" className="cursor-pointer">Mostrar en página web</Label>
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }}>Cancelar</Button>
              <Button type="submit" className="bg-[#C9A961]">Guardar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog />
    </div>
  );
};

export default ServicesConfig;